import { AsyncPipe, KeyValuePipe } from '@angular/common';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { combineLatest, type Observable, of } from 'rxjs';
import { map, switchMap, tap } from 'rxjs';

import {
  PermissionDirective,
  permissions,
  SearchComponent as EvcSearchComponent,
} from '@evc/platform';
import {
  BreadcrumbComponent,
  BreadcrumbItem,
  ButtonComponent,
  SelectComponent,
} from '@evc/web-components';
import { I18nService, TranslatePipe } from '@shared/services/i18n/i18n.service';

import { PanelService } from '@app/services';
import { resetBreadcrumbState } from '@app/store/actions/breadcrumb/breadcrumb.action';
import {
  deleteItems,
  setCurrentFolder,
  setSelectedItems,
} from '@app/store/actions/file/file.action';
import { getBreadcrumb, getRouterData, isPageLoading } from '@app/store/selectors';
import type { FileItem } from '@app/types/file-item.type';

@Component({
  selector: 'evc-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [
    ReactiveFormsModule,
    BreadcrumbComponent,
    ButtonComponent,
    EvcSearchComponent,
    PermissionDirective,
    TranslatePipe,
    KeyValuePipe,
    AsyncPipe,
    SelectComponent,
  ],
})
export class HeaderComponent implements OnInit {
  readonly #store = inject(Store);
  readonly #panelService = inject(PanelService);
  readonly #router = inject(Router);

  protected PERMISSIONS = permissions;

  #destroyRef = inject(DestroyRef);
  #i18nService = inject(I18nService);

  loading$: Observable<boolean> = this.#store.pipe(select(isPageLoading));
  folders$: Observable<FileItem[]> = this.#store.pipe(select(getBreadcrumb));
  baseTitle$: Observable<{ name: string; locked: boolean }> = this.#store.pipe(
    select(getRouterData),
    map((data: { id: string; base: string; locked: boolean }) => ({
      id: data?.id,
      name: data?.base,
      locked: data?.locked,
    })),
  );

  breadcrumbItems$: Observable<BreadcrumbItem[]> = combineLatest([
    this.baseTitle$,
    this.folders$,
  ]).pipe(
    map(([base, folders]) => {
      const items = (folders ?? []) as BreadcrumbItem[];

      return base?.name ? [{ id: '', name: this.#i18nService.t(base.name) }, ...items] : items;
    }),
  );

  base: Partial<FileItem>;
  locked = false;
  routeId: string;

  isPannelOpen = toSignal(this.#panelService.rightPanelVisibility$);
  title = toSignal(this.#getTitle$());

  #getTitle$() {
    return this.folders$.pipe(
      switchMap((folders) =>
        folders.length
          ? of(folders.at(-1)!).pipe(map(({ name }) => name))
          : this.baseTitle$.pipe(map(({ name }) => this.#i18nService.t(name))),
      ),
      takeUntilDestroyed(this.#destroyRef),
    );
  }

  ngOnInit(): void {
    /** DIRTY
     * - used to render title in html SIMPLY to trigger the tap action
     * - now at least cleaner but bad design - title observable should not be what trigger the base here
     */
    this.baseTitle$
      .pipe(
        tap(({ id, name, locked }: { id: string; name: string; locked: boolean }) => {
          this.base = name ? { name } : {};
          this.locked = locked;
          this.routeId = id;
        }),
      )
      .subscribe();
  }

  onInfoClicked(): void {
    this.#panelService.toggleRightPanelVisibility();
  }

  onCrumbSelected(item: Partial<FileItem>): void {
    if (!item.id) {
      this.onBaseSelected();
      return;
    }

    this.onFolderSelected(item);
  }

  onBaseSelected(): void {
    this.#router.navigate([`/${this.getBase(this.#router.url)}`]).then(() => {
      this.#store.dispatch(setCurrentFolder({ folder: null }));
      this.#store.dispatch(setSelectedItems({ items: [] }));
      this.#store.dispatch(resetBreadcrumbState());
    });
  }

  onFolderSelected(folder: Partial<FileItem>): void {
    this.#router.navigate([`/${this.getBase(this.#router.url)}`, folder.id]).then(() => {
      this.#store.dispatch(setCurrentFolder({ folder }));
      this.#store.dispatch(setSelectedItems({ items: [] }));
    });
  }

  onDeleteItems(items: FileItem[]): void {
    this.#store.dispatch(deleteItems({ items }));
  }

  private getBase(url: string): string[] {
    const split: string[] = url.split('/').filter((p) => !!p);
    split.pop();

    return split;
  }
}
